import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { SectorBadge } from '@/components/ui/sector-badge';
import { ProfileCompleteness } from '@/components/profile/ProfileCompleteness';
import { AccountInfoCard } from '@/components/profile/AccountInfoCard';
import { ApplicationStatsCard } from '@/components/profile/ApplicationStatsCard';
import { ResumeCard } from '@/components/profile/ResumeCard';
import { QuickLinksCard } from '@/components/profile/QuickLinksCard';
import {
  Loader2, Pencil, Save, X, User, Briefcase, Globe, MapPin,
  Key, ExternalLink, Mail, Phone, Languages as LanguagesIcon
} from 'lucide-react';

const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;
const LANGUAGES_LIST = ['en', 'fr', 'pt', 'sw', 'ln'] as const;
const OPEN_TO = ['employment', 'consulting', 'board_roles', 'projects'] as const;

interface ProfessionalData {
  id: string;
  headline: string | null;
  bio: string | null;
  skills: string[];
  sectors: string[];
  languages: string[];
  years_experience: number | null;
  open_to: string[];
  linkedin_url: string | null;
  resume_url: string | null;
}

export default function MyProfile() {
  const { t } = useTranslation();
  const { user, profile, role, loading: authLoading, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [professional, setProfessional] = useState<ProfessionalData | null>(null);

  // Editable fields - personal info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');

  // Editable fields - professional info
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [sectors, setSectors] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [yearsExperience, setYearsExperience] = useState('');
  const [openTo, setOpenTo] = useState<string[]>([]);
  const [linkedinUrl, setLinkedinUrl] = useState('');

  // Password reset
  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (!authLoading && role === 'company') {
      navigate('/profile/company');
      return;
    }
    if (user && profile) {
      fetchProfessionalData();
    }
  }, [user, profile, role, authLoading]);

  const fetchProfessionalData = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('professionals')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        const prof: ProfessionalData = {
          id: data.id,
          headline: data.headline,
          bio: data.bio,
          skills: (data.skills as string[]) || [],
          sectors: (data.sectors as string[]) || [],
          languages: (data.languages as string[]) || [],
          years_experience: data.years_experience,
          open_to: (data.open_to as string[]) || [],
          linkedin_url: data.linkedin_url,
          resume_url: data.resume_url,
        };
        setProfessional(prof);
      }
    } catch (e) {
      // No professional profile yet
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    setFirstName(profile?.first_name || '');
    setLastName(profile?.last_name || '');
    setPhone(profile?.phone || '');
    setCountry(profile?.country || '');
    setHeadline(professional?.headline || '');
    setBio(professional?.bio || '');
    setSkills([...(professional?.skills || [])]);
    setSectors([...(professional?.sectors || [])]);
    setLanguages([...(professional?.languages || [])]);
    setYearsExperience(professional?.years_experience?.toString() || '');
    setOpenTo([...(professional?.open_to || [])]);
    setLinkedinUrl(professional?.linkedin_url || '');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setSkillInput('');
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !skills.includes(skill)) {
      setSkills([...skills, skill]);
      setSkillInput('');
    }
  };

  const toggleItem = (item: string, array: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const handleSave = async () => {
    if (!user || !professional) return;
    setSaving(true);
    try {
      // Update profile table
      const { error: profileError } = await updateProfile({
        first_name: firstName,
        last_name: lastName,
        phone: phone || null,
        country: country || null,
      } as any);
      if (profileError) throw profileError;

      // Update professional table
      const { error: profError } = await supabase
        .from('professionals')
        .update({
          headline: headline || null,
          bio: bio || null,
          skills,
          sectors,
          languages,
          years_experience: yearsExperience ? parseInt(yearsExperience) : null,
          open_to: openTo,
          linkedin_url: linkedinUrl || null,
        })
        .eq('id', professional.id);
      if (profError) throw profError;

      // Refresh local state
      setProfessional(prev => prev ? {
        ...prev,
        headline: headline || null,
        bio: bio || null,
        skills,
        sectors,
        languages,
        years_experience: yearsExperience ? parseInt(yearsExperience) : null,
        open_to: openTo,
        linkedin_url: linkedinUrl || null,
      } : null);

      setEditing(false);
      toast({ title: t('myProfile.saveSuccess') });
    } catch (error) {
      console.error(error);
      toast({ title: t('myProfile.saveError'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword.length < 8) {
      toast({ title: t('auth.errors.passwordTooShort'), variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t('auth.errors.passwordsDontMatch'), variant: 'destructive' });
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: t('auth.passwordUpdated') });
      setChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({ title: error.message || t('auth.errors.genericError'), variant: 'destructive' });
    } finally {
      setPasswordSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!professional) {
    return (
      <Layout>
        <div className="container max-w-2xl py-12 text-center">
          <h1 className="font-heading text-2xl font-bold mb-4">{t('myProfile.title')}</h1>
          <p className="text-muted-foreground mb-6">{t('professional.dashboard.setupPrompt')}</p>
          <Button onClick={() => navigate('/profile/professional/setup')}>
            {t('professional.dashboard.setupButton')}
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-3xl py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold">{t('myProfile.title')}</h1>
        </div>

        {/* Profile Completeness */}
        <ProfileCompleteness profile={profile} professional={professional} />

        {/* Header actions */}
        <div className="flex items-center justify-end">
          {!editing ? (
            <Button variant="outline" onClick={startEditing}>
              <Pencil className="mr-2 h-4 w-4" />
              {t('common.edit')}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={cancelEditing}>
                <X className="mr-1 h-4 w-4" />
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                {t('common.save')}
              </Button>
            </div>
          )}
        </div>

        {/* Account Information */}
        {profile && role && user && (
          <AccountInfoCard profile={{ email: profile.email, created_at: user.created_at, language: profile.language }} role={role} />
        )}

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-primary" />
              {t('myProfile.personalInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('auth.signup.firstName')}</Label>
                  <Input value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.signup.lastName')}</Label>
                  <Input value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.signup.phone')}</Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.signup.country')}</Label>
                  <Input value={country} onChange={e => setCountry(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{profile?.first_name} {profile?.last_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{profile?.email}</span>
                </div>
                {profile?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.phone}</span>
                  </div>
                )}
                {profile?.country && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{profile.country}</span>
                  </div>
                )}
                {profile?.is_diaspora && (
                  <div className="sm:col-span-2">
                    <Badge variant="secondary" className="bg-teal-100 text-teal-800">
                      🌍 {t('myProfile.diaspora')}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Professional Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="h-5 w-5 text-primary" />
              {t('myProfile.professionalInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {editing ? (
              <>
                <div className="space-y-2">
                  <Label>{t('professional.profile.headline')}</Label>
                  <Input value={headline} onChange={e => setHeadline(e.target.value)} placeholder={t('professional.profile.headlinePlaceholder')} />
                </div>
                <div className="space-y-2">
                  <Label>{t('professional.profile.bio')}</Label>
                  <Textarea value={bio} onChange={e => setBio(e.target.value)} rows={4} />
                </div>
                <div className="space-y-2">
                  <Label>{t('professional.profile.experience')}</Label>
                  <Input type="number" min="0" max="50" value={yearsExperience} onChange={e => setYearsExperience(e.target.value)} className="max-w-[120px]" />
                </div>
                <div className="space-y-2">
                  <Label>{t('professional.profile.skills')}</Label>
                  <div className="flex gap-2">
                    <Input value={skillInput} onChange={e => setSkillInput(e.target.value)} placeholder={t('professional.profile.skillsPlaceholder')} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())} />
                    <Button type="button" variant="outline" onClick={addSkill}>Add</Button>
                  </div>
                  {skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {skills.map(skill => (
                        <Badge key={skill} variant="secondary" className="gap-1">
                          {skill}
                          <button type="button" onClick={() => setSkills(skills.filter(s => s !== skill))} className="hover:text-destructive">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <Label>{t('professional.profile.sectors')}</Label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {SECTORS.map(sector => (
                      <Label key={sector} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${sectors.includes(sector) ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                        <Checkbox checked={sectors.includes(sector)} onCheckedChange={() => toggleItem(sector, sectors, setSectors)} />
                        <span className="text-sm">{t(`sectors.${sector}`)}</span>
                      </Label>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <Label>{t('professional.profile.languages')}</Label>
                  <div className="flex flex-wrap gap-3">
                    {LANGUAGES_LIST.map(lang => (
                      <Label key={lang} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${languages.includes(lang) ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                        <Checkbox checked={languages.includes(lang)} onCheckedChange={() => toggleItem(lang, languages, setLanguages)} />
                        <span className="text-sm">{t(`languages.${lang}`)}</span>
                      </Label>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <Label>{t('professional.profile.openTo')}</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {OPEN_TO.map(item => (
                      <Label key={item} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${openTo.includes(item) ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                        <Checkbox checked={openTo.includes(item)} onCheckedChange={() => toggleItem(item, openTo, setOpenTo)} />
                        <span className="text-sm">{t(`openTo.${item}`)}</span>
                      </Label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('professional.profile.linkedin')}</Label>
                  <Input type="url" value={linkedinUrl} onChange={e => setLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/..." />
                </div>
              </>
            ) : (
              <>
                {professional.headline && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('professional.profile.headline')}</p>
                    <p className="font-medium">{professional.headline}</p>
                  </div>
                )}
                {professional.bio && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('professional.profile.bio')}</p>
                    <p className="text-sm mt-1">{professional.bio}</p>
                  </div>
                )}
                {professional.years_experience != null && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('professional.profile.experience')}</p>
                    <p className="font-medium">{professional.years_experience} {t('talent.yearsExp')}</p>
                  </div>
                )}
                {professional.skills.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{t('professional.profile.skills')}</p>
                    <div className="flex flex-wrap gap-2">
                      {professional.skills.map(skill => (
                        <Badge key={skill} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {professional.sectors.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{t('professional.profile.sectors')}</p>
                    <div className="flex flex-wrap gap-2">
                      {professional.sectors.map(sector => (
                        <SectorBadge key={sector} sector={sector as any} />
                      ))}
                    </div>
                  </div>
                )}
                {professional.languages.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{t('professional.profile.languages')}</p>
                    <div className="flex items-center gap-2">
                      <LanguagesIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{professional.languages.filter(l => LANGUAGES_LIST.includes(l as any)).map(l => t(`languages.${l}`)).join(', ')}</span>
                    </div>
                  </div>
                )}
                {professional.open_to.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{t('professional.profile.openTo')}</p>
                    <div className="flex flex-wrap gap-2">
                      {professional.open_to.map(item => (
                        <Badge key={item} variant="outline">{t(`openTo.${item}`)}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {professional.linkedin_url && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a href={professional.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                      LinkedIn <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Application Stats */}
        <ApplicationStatsCard professionalId={professional.id} />

        {/* Resume */}
        <ResumeCard
          resumeUrl={professional.resume_url}
          professionalId={professional.id}
          userId={user!.id}
          onResumeUpdated={(url) => setProfessional(prev => prev ? { ...prev, resume_url: url } : null)}
        />

        {/* Quick Links */}
        <QuickLinksCard />

        {/* Security / Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Key className="h-5 w-5 text-primary" />
              {t('myProfile.security')}
            </CardTitle>
            <CardDescription>{t('myProfile.securityDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {changingPassword ? (
              <div className="space-y-4 max-w-sm">
                <div className="space-y-2">
                  <Label>{t('auth.newPassword')}</Label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.confirmNewPassword')}</Label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handlePasswordReset} disabled={passwordSaving}>
                    {passwordSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auth.updatePassword')}
                  </Button>
                  <Button variant="ghost" onClick={() => { setChangingPassword(false); setNewPassword(''); setConfirmPassword(''); }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setChangingPassword(true)}>
                <Key className="mr-2 h-4 w-4" />
                {t('myProfile.changePassword')}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
